import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface UIState {
  sidebarOpen: boolean;
  theme: 'dark' | 'light';
  notifications: Notification[];
  modal: {
    isOpen: boolean;
    type: 'wallet' | 'settings' | 'fairness' | 'history' | null;
    data?: any;
  };
  sound: {
    enabled: boolean;
    volume: number;
  };
}

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: number;
  duration?: number;
}

const initialState: UIState = {
  sidebarOpen: false,
  theme: 'dark',
  notifications: [],
  modal: {
    isOpen: false,
    type: null,
    data: null,
  },
  sound: {
    enabled: true,
    volume: 0.5,
  },
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    setTheme: (state, action: PayloadAction<'dark' | 'light'>) => {
      state.theme = action.payload;
    },
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'timestamp'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: Date.now().toString(),
        timestamp: Date.now(),
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    openModal: (state, action: PayloadAction<{ type: UIState['modal']['type']; data?: any }>) => {
      state.modal.isOpen = true;
      state.modal.type = action.payload.type;
      state.modal.data = action.payload.data;
    },
    closeModal: (state) => {
      state.modal.isOpen = false;
      state.modal.type = null;
      state.modal.data = null;
    },
    setSoundEnabled: (state, action: PayloadAction<boolean>) => {
      state.sound.enabled = action.payload;
    },
    setSoundVolume: (state, action: PayloadAction<number>) => {
      state.sound.volume = Math.max(0, Math.min(1, action.payload));
    },
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  setTheme,
  addNotification,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
  setSoundEnabled,
  setSoundVolume,
} = uiSlice.actions;

export default uiSlice.reducer;